import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSAPCDC } from '../hooks/useSAPCDC';
import { Button } from '../../../components/ui/button';
import Header from '../../../components/ui/Header';
import Footer from '../../../components/ui/Footer';
import { ArrowLeft, Mail, CheckCircle, XCircle } from 'lucide-react';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { forgotPassword, isLoading, error, clearError } = useSAPCDC();
  
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const validateEmail = (email) => {
    if (!email) {
      return 'Email address is required';
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    // Clear errors when user starts typing
    if (emailError) {
      setEmailError('');
    }
    if (submitError) {
      setSubmitError('');
    }
    if (error) {
      clearError();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email
    const validation = validateEmail(email);
    if (validation) {
      setEmailError(validation);
      return;
    }
    
    try {
      console.log('\n🔐 [Forgot Password] Starting password reset request...');
      console.log('📧 [Forgot Password] Email:', email);
      
      const result = await forgotPassword(email);
      
      if (result.success) {
        console.log('✅ [Forgot Password] Reset email sent successfully');
        setIsEmailSent(true);
        setSuccessMessage(result.message || 'Password reset email sent successfully. Please check your email for instructions.');
        setSubmitError('');
      } else {
        console.error('❌ [Forgot Password] Reset request failed:', result.error);
        setSubmitError(result.error || 'Failed to send password reset email. Please try again.');
        setSuccessMessage('');
        setIsEmailSent(false);
      }
    } catch (err) {
      console.error('❌ [Forgot Password] Unexpected error:', err);
      setSubmitError('An unexpected error occurred. Please try again.');
      setSuccessMessage('');
      setIsEmailSent(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/');
  };

  const handleTryAgain = () => {
    setIsEmailSent(false);
    setSuccessMessage('');
    setSubmitError('');
    setEmail('');
    setEmailError('');
    clearError();
  };

  return (
    <section className="min-h-screen bg-white flex flex-col justify-between">
      <Header />
      
      {/* Main Content */}
      <div className="flex flex-1 items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-8">
          
          {/* Back to Login Button */}
          <button
            onClick={handleBackToLogin}
            className="flex items-center text-brand-blue hover:text-brand-darkBlue mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login
          </button>

          {!isEmailSent ? (
            // Email Request Form
            <div>
              <div className="text-center mb-6">
                <div className="flex justify-center mb-4">
                  <div className="bg-brand-blue bg-opacity-10 p-3 rounded-full">
                    <Mail className="w-8 h-8 text-brand-blue" />
                  </div>
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Forgot Password?</h1>
                <p className="text-gray-600">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-left text-sm font-medium text-gray-700 mb-2">
                    Email Address <span className="text-error-500">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder="Enter your email address"
                    className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-primary-400 focus:border-primary-400 outline-none transition-colors ${
                      emailError ? 'border-error-500' : 'border-gray-300'
                    }`}
                  />
                  {emailError && <p className="text-error-500 text-sm mt-1">{emailError}</p>}
                </div>

                {/* Display error messages */}
                {(submitError || error) && (
                  <div className="bg-error-50 border border-error-200 rounded-md p-3 flex items-start">
                    <XCircle className="w-5 h-5 text-error-500 mr-3 flex-shrink-0 mt-0.5" />
                    <div className="text-error-700 text-sm">
                      {submitError || error}
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full text-white py-3 text-lg font-semibold rounded-md transition-colors bg-brand-blue hover:bg-brand-darkBlue border-none shadow-sm"
                  style={{ 
                    height: '50px', 
                    backgroundColor: isLoading ? '#6B7280' : '#2E7FA1',
                    fontSize: '16px', 
                    boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.04)',
                    color: 'white',
                    border: 'none'
                  }}
                >
                  {isLoading ? 'Sending Reset Email...' : 'Send Reset Email'}
                </Button>
              </form>
            </div>
          ) : (
            // Success Message
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="bg-green-100 p-3 rounded-full">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
              </div>
              
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Check Your Email</h1>
              
              <p className="text-gray-600 mb-4">
                We've sent a password reset link to:
              </p>
              
              <p className="text-lg font-semibold text-gray-900 mb-6">
                {email}
              </p>
              
              {successMessage && (
                <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                    <div className="text-green-700 text-sm">
                      {successMessage}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="space-y-4">
                <div className="text-sm text-gray-600">
                  <p className="mb-2">
                    <strong>Next steps:</strong>
                  </p>
                  <ul className="text-left space-y-1 list-disc list-inside">
                    <li>Check your email inbox (and spam folder)</li>
                    <li>Click the password reset link in the email</li>
                    <li>Follow the instructions to create a new password</li>
                    <li>Return here to login with your new password</li>
                  </ul>
                </div>
                
                <div className="border-t pt-4">
                  <p className="text-sm text-gray-600 mb-4">
                    Didn't receive the email? Check your spam folder or try again.
                  </p>
                  
                  <div className="space-y-3">
                    <Button
                      onClick={handleTryAgain}
                      variant="outline"
                      className="w-full border-2 border-brand-blue text-brand-blue py-2 text-sm font-medium rounded-md transition-colors bg-white hover:bg-primary-50"
                      style={{ 
                        borderColor: '#2E7FA1',
                        color: '#2E7FA1',
                        backgroundColor: 'white'
                      }}
                    >
                      Send Another Email
                    </Button>
                    
                    <Button
                      onClick={handleBackToLogin}
                      className="w-full text-white py-2 text-sm font-medium rounded-md transition-colors bg-brand-blue hover:bg-brand-darkBlue border-none"
                      style={{ 
                        backgroundColor: '#2E7FA1',
                        color: 'white',
                        border: 'none'
                      }}
                    >
                      Back to Login
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </section>
  );
};

export default ForgotPassword;